import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ProjectService } from "src/app/shared/services/project.service";
import { NgxSpinnerService } from "ngx-spinner";
import { MatDialog } from "@angular/material/dialog";
import { DatePipe } from "@angular/common";
import { RisklogDialogComponent } from "./risklog-dialog/risklog-dialog.component";
import textConfiguration from "src/assets/static-text-configuration.json";

@Component({
  selector: "app-risklog-heatmap",
  templateUrl: "./risklog-heatmap.component.html",
  styleUrls: ["./risklog-heatmap.component.scss"],
  providers: [DatePipe],
})
export class RisklogHeatmapComponent implements OnInit, OnChanges {
  @Input() recommendedRisks: any;
  @Input() riskArrayHeatMap: any;
  @Input() selectedRisks: any[] = [];
  @Input() projectId: any;
  @Input() iframeEmbedded = false;
  @Output() riskChanged = new EventEmitter<any>();
  riskMetric: any;
  user: any;
  commonText: any = (textConfiguration as any).common;

  constructor(
    public fb: FormBuilder,
    private readonly projectService: ProjectService,
    private readonly spinner: NgxSpinnerService,
    private readonly dialog: MatDialog,
    public datepipe: DatePipe
  ) { }


  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes["riskArrayHeatMap"] && this.riskArrayHeatMap?.length) ||
      (changes["recommendedRisks"] && this.recommendedRisks?.length)
    ) {
      this.mergeRecommendedRisks();
    }
    if (changes["riskArrayHeatMap"] || changes["selectedRisks"]) {
      this.enrichRiskNodes();
    }
  }
  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem("permission") ?? "{}");

    if (this.recommendedRisks?.length) {
      this.mergeRecommendedRisks();
    }
    this.enrichRiskNodes();
  }

  /** Stamps risk_impact and residual_risk_score from selectedRisks onto each risk node. */
  private enrichRiskNodes() {
    if (!this.riskArrayHeatMap?.length || !this.selectedRisks?.length) return;
    this.riskArrayHeatMap.forEach((domain: any) => {
      const subdomains = domain.children ?? domain.sub_domains ?? [];
      subdomains.forEach((sub: any) => {
        const risks = sub.children ?? sub.risks ?? [];
        risks.forEach((risk: any) => {
          const match = this.selectedRisks.find((s: any) => s.risk_id === risk.uuid);
          if (match) {
            risk.risk_type = match.risk_type;
            risk.risk_impact = match.risk_impact;
            risk.residual_risk_score = match.residual_risk_score ?? null;
            risk.status = match.status;
            risk.risk_response = match.risk_response;
          }
        });
      });
    });
  }

  getFilteredArray(array: any) {
    if (array !== undefined) {
      return array.filter((e: any) => e.selected);
    } else {
      return [];
    }
  }

  getBackgroundColor(color: string) {
    if (color === "green") {
      return "linear-gradient(0deg, #41a064ff, #5edd8dff)";
    } else if (color === "amber") {
      return "linear-gradient(45deg, #ffc206, #d5c487)";
    } else if (color === "red") {
      return "linear-gradient(45deg, #DD2C54, #f70029a5)";
    } else {
      return "grey";
    }
  }

  getTrucatedName(
    subdomain: string,
    shortDescription: string,
    riskName: string
  ) {
    if (!shortDescription) {
      shortDescription = riskName;
    }
    if (shortDescription.length > subdomain.length) {
      let subStrThreshold = 10;
      if (subdomain.length - 1 > 10) {
        subStrThreshold = subdomain.length - 1;
      }
      return shortDescription.substring(0, subStrThreshold) + "...";
    } else {
      return shortDescription;
    }
  }
  mergeRecommendedRisks() {
    this.recommendedRisks.forEach((domainBlock: any) => {

      domainBlock.subdomainList.forEach((sdBlock: any) => {
        const subdomain = sdBlock.subdomain;

        sdBlock.risks.forEach((recRisk: any) => {

          let hmDomain = this.riskArrayHeatMap.find(
            (d: any) => d.uuid === domainBlock.domain.uuid
          );
          if (!hmDomain) return;

          hmDomain.selected = true;

          let hmSubdomain = hmDomain.children.find(
            (sd: any) => sd.uuid === subdomain.uuid
          );

          if (!hmSubdomain) {
            hmSubdomain = {
              ...subdomain,
              selected: true,
              children: []
            };
            hmDomain.children.push(hmSubdomain);
          }

          hmSubdomain.selected = true;

          let hmRisk = hmSubdomain.children.find(
            (r: any) => r.uuid === recRisk.uuid
          );

          const isMissing = !hmRisk;

          if (isMissing) {
            hmRisk = {
              ...recRisk,
              selected: true,
              color: "grey",
              is_recommended_only: true,
              status: ""
            };
            hmSubdomain.children.push(hmRisk);
          }

          if (hmRisk.project_risk_id) {
            hmRisk.is_recommended_only = false;
          }

          else {
            hmRisk.color = "grey";
            hmRisk.is_recommended_only = true;
          }

          hmRisk.selected = true;

        });
      });
    });
    this.recalculateColors();
  }

  recalculateColors() {
    this.riskArrayHeatMap.forEach((domain: any) => {

      const subdomains = domain.children ?? domain.sub_domains ?? [];

      subdomains.forEach((sub: any) => {

        const risks = sub.children ?? sub.risks ?? [];

        const riskColors = risks.map((r: any) => r.color);

        if (riskColors.length && riskColors.every((c: any) => c === "grey")) {
          sub.color = "grey";
        } else {
          if (riskColors.includes("red")) sub.color = "red";
          else if (riskColors.includes("amber")) sub.color = "amber";
          else if (riskColors.includes("green")) sub.color = "green";
          else sub.color = "grey";
        }
      });

      const subColors = subdomains.map((s: any) => s.color);

      if (subColors.length && subColors.every((c: any) => c === "grey")) {
        domain.color = "grey";
      } else {
        if (subColors.includes("red")) domain.color = "red";
        else if (subColors.includes("amber")) domain.color = "amber";
        else if (subColors.includes("green")) domain.color = "green";
        else domain.color = "grey";
      }

    });
  }

  onClickRisk(domain: any, sub: any, risk: any, modal: any) {

    if (risk.is_recommended_only) {
      return;
    }
    if (this.user?.is_vendor || this.iframeEmbedded) {
      return;
    }
    this.riskMetric = {
      domain: domain.name,
      subdomain: sub.name,
      risk: risk.name,
      risk_id: risk.uuid,
      risk_response: "",
      id: risk.id,
    };
    this.projectService
      .getRiskProjectMapped(this.projectId, this.riskMetric.risk_id)
      .subscribe((res: any) => {
        if (res) {
          this.riskMetric.risk_response = res.risk_response;
        }
        const projectDetails: any = JSON.parse(
          localStorage.getItem("program") ?? "{}"
        );
        const selectedProjectName = JSON.parse(
          localStorage.getItem("selectedProject") ?? "{}"
        );
        const dlg = this.dialog.open(RisklogDialogComponent, {
          width: "80vw",
          height: "90vh",
          maxHeight: "100vh",
          disableClose: true,
          autoFocus: false,
          data: {
            riskMetric: this.riskMetric,
            res,
            projectId: this.projectId,
            breadCrumbData: [
              projectDetails.portfolio_name,
              projectDetails.subportfolio_name,
              projectDetails.program_name,
              selectedProjectName.project_name,
            ],
          },
        });
        dlg.afterClosed().subscribe((data: any) => {
          if (data) {
            this.updateRiskStatus(data.riskId, data.res);
            this.riskChanged.emit({
              riskId: data.riskId,
              riskResponse: data.res.risk_response,
            });
          }
        });
      });
  }

  updateRiskStatus(riskId: any, res: any) {
    let statusUpdated = false;
    for (const domain of this.riskArrayHeatMap) {
      for (const subdomain of domain.sub_domains) {
        const risk = subdomain.risks.find((item: any) => item.uuid === riskId);
        if (risk) {
          risk.status = res.risk_response;
          statusUpdated = true;
          break;
        }
      }
      if (statusUpdated) {
        break;
      }
    }
  }

  hasAnyVisibleRisks(): boolean {
    return this.getFilteredArray(this.riskArrayHeatMap).some((d: any) =>
      this.getFilteredArray(d.children).some((sd: any) =>
        this.getFilteredArray(sd.children).length > 0
      )
    );
  }

  closeDialog() {
    this.dialog.closeAll();
  }
}
